import { useState } from 'react'
import { ListView, ViewRenderer, type FormatContext, type FetchUserOptions, type FetchEntityOptions, type FetchEntityById } from './ViewRenderer'
import { evaluateShowIf } from './evaluateShowIfExpr'
import type { EntityFieldMeta, ViewDefinition } from './viewTypes'

/** Native platform APIs bind data/actions here; entity-generated definitions own the UI. */
export function GeneratedRecord({ view, fields, record, onChange, formatting, fetchUserOptions, fetchEntityOptions, fetchEntityById }: {
    view: ViewDefinition; fields: EntityFieldMeta; record: Record<string, unknown>
    formatting?: FormatContext | undefined
    onChange?: ((record: Record<string, unknown>) => void) | undefined
    fetchUserOptions?: FetchUserOptions | undefined
    fetchEntityOptions?: FetchEntityOptions | undefined
    fetchEntityById?: FetchEntityById | undefined
}) {
    const binding = view.context?.find(c => !c.collection && c.entityType === view.entityType)?.name
    if (!binding) throw new Error('Generated record view requires a record context')
    const context = { [binding]: record }
    return <ViewRenderer view={view} context={context} entityFields={fields}
        editable={!!onChange} draft={context} formatting={formatting} fetchUserOptions={fetchUserOptions}
        fetchEntityOptions={fetchEntityOptions} fetchEntityById={fetchEntityById}
        onChange={onChange ? next => onChange(next[binding] as Record<string, unknown>) : undefined} />
}

export function GeneratedActions({ view, record, permissions, pending, disabled, disabledActions, onAction }: {
    view: ViewDefinition; record: Record<string, unknown>; permissions: ReadonlySet<string>
    pending?: boolean | undefined; disabled?: boolean | undefined
    disabledActions?: ReadonlySet<string> | undefined
    onAction: (target: string, record: Record<string, unknown>) => void
}) {
    return <div className="flex flex-wrap items-center gap-2">{(view.pageActions ?? []).filter(a =>
        !a.hidden && (!a.permission || permissions.has(a.permission)) && evaluateShowIf(a.showIf, record, permissions)
    ).map(a => <button key={a.target ?? a.label} type="button"
        className="rounded border border-neutral-300 dark:border-slate-600 px-3 py-1.5 text-sm font-medium hover:bg-neutral-100 dark:hover:bg-slate-800 disabled:opacity-50 min-h-11"
        disabled={pending || disabled || !!a.target && disabledActions?.has(a.target)} onClick={e => {
            e.stopPropagation()
            if (a.target && !pending && !disabled && !disabledActions?.has(a.target)) onAction(a.target, record)
        }}>{a.label}</button>)}</div>
}

export function GeneratedList({ view, fields, rows, loading, permissions, pending, onAction, formatting, page, hasMore, onPage, showTitle }: {
    view: ViewDefinition; fields: EntityFieldMeta; rows: Record<string, unknown>[]
    permissions: ReadonlySet<string>; loading?: boolean | undefined; pending?: boolean | undefined
    onAction: (target: string, record: Record<string, unknown>) => void
    formatting?: FormatContext | undefined; page: number; hasMore: boolean
    onPage: (page: number) => void
    showTitle?: boolean
}) {
    const [selectedId, setSelectedId] = useState<unknown>(null)
    const selected = rows.find(row => row.id === selectedId)
    return <>
        {showTitle && <h2 className="font-semibold text-neutral-900 dark:text-slate-100">{view.displayName}</h2>}
        <ListView view={view} rows={rows} entityFields={fields} loading={loading} fmt={formatting} onRowClick={row => setSelectedId(row.id)}
            rowAction={view.pageActions?.length ? row => <GeneratedActions view={view} record={row} permissions={permissions} pending={pending} onAction={onAction} /> : undefined} />
        {selected && <section className="space-y-3">
            <button type="button" className="text-sm underline" onClick={() => setSelectedId(null)}>Close details</button>
            <GeneratedRecord view={view} fields={fields} record={selected} formatting={formatting} />
        </section>}
        <div className="flex items-center justify-end gap-3 text-sm">
            <button className="rounded border px-3 py-1.5 disabled:opacity-40" disabled={loading || page === 1} onClick={() => onPage(page - 1)}>‹ Prev</button>
            <span>Page {page}</span>
            <button className="rounded border px-3 py-1.5 disabled:opacity-40" disabled={loading || !hasMore} onClick={() => onPage(page + 1)}>Next ›</button>
        </div>
    </>
}

export function ViewFeedback({ message, error = false }: { message: string; error?: boolean }) {
    return <div role={error ? 'alert' : 'status'} className={`rounded border px-4 py-3 text-sm ${error
        ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200'
        : 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200'}`}>{message}</div>
}
